import BadgeMask from '-/Badge/BadgeMask';
import Avatar from '-/Custom/Avatar';
import * as Placeholder from '-/Custom/Placeholder';
import { Stack, useRouter } from 'expo-router';
import { useState } from 'react';
import { ScrollView } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { useQuery } from 'urql';
import FadeInContent from '@/components/Animation/FadeInContent';
import SearchBar from '@/components/Custom/SearchBar';
import Box from '@/components/UI/Box';
import PressableRow from '@/components/UI/PressableRow';
import Text from '@/components/UI/Text';
import Icon from '@/components/Unistyles/Icon';
import { graphql } from '@/gql/graphql';
import {
  type OrganizationMemberBadgeChoiceRow,
  toOrganizationMemberBadgeChoiceRow,
} from '@/screens/OrganizationDetailMembers/OrganizationDetailMembers.utils';
import useMemberAssignmentStore, {
  getMemberAssignmentSelectedLabel,
  hasMemberAssignmentContext,
  setMemberAssignmentBadgeId,
} from '@/stores/useMemberAssignmentStore';
import { filterBySearchText } from '@/utils/filterBySearchText';

const BADGE_FILTER_LIMIT = 250;

const GetMemberActionOrganizationBadgeChoices = graphql(/* GraphQL */ `
  query GetMemberActionOrganizationBadgeChoices(
    $organizationId: uuid!
    $badgeFilterLimit: Int!
    $personIds: [String!]!
  ) {
    relevant_badge(
      args: { event_id: $organizationId }
      limit: $badgeFilterLimit
      order_by: { title: asc }
    ) {
      id
      title
      color
      badge_assignees_aggregate(
        where: {
          event_id: { _eq: $organizationId }
          person_id: { _in: $personIds }
        }
      ) {
        aggregate {
          count
        }
      }
    }
  }
`);

const GetMemberActionTeamBadgeChoices = graphql(/* GraphQL */ `
  query GetMemberActionTeamBadgeChoices(
    $teamId: uuid!
    $badgeFilterLimit: Int!
    $personIds: [String!]!
  ) {
    relevant_badge(
      args: { team_id: $teamId }
      limit: $badgeFilterLimit
      order_by: { title: asc }
    ) {
      id
      title
      color
      badge_assignees_aggregate(
        where: { team_id: { _eq: $teamId }, person_id: { _in: $personIds } }
      ) {
        aggregate {
          count
        }
      }
    }
  }
`);

export default function MemberActionAssignBadge() {
  const { push } = useRouter();
  const activeAssignment = useMemberAssignmentStore(
    (state) => state.activeAssignment,
  );
  const [searchTerm, setSearchTerm] = useState('');
  const organizationId = activeAssignment?.organizationId;
  const teamId = activeAssignment?.teamId;
  const personIds = activeAssignment?.personId
    ? [activeAssignment.personId]
    : (activeAssignment?.selectedPersonIds ?? []);
  const testIDPrefix = activeAssignment?.testIDPrefix ?? 'MemberAction';
  const selectedLabel = getMemberAssignmentSelectedLabel(activeAssignment);
  const hasContext = hasMemberAssignmentContext(activeAssignment);
  const [{ data: organizationData, fetching: fetchingOrganizationChoices }] =
    useQuery({
      query: GetMemberActionOrganizationBadgeChoices,
      variables: {
        organizationId: organizationId || '',
        badgeFilterLimit: BADGE_FILTER_LIMIT,
        personIds,
      },
      requestPolicy: 'cache-and-network',
      pause: !hasContext || !organizationId || !!teamId,
    });
  const [{ data: teamData, fetching: fetchingTeamChoices }] = useQuery({
    query: GetMemberActionTeamBadgeChoices,
    variables: {
      teamId: teamId || '',
      badgeFilterLimit: BADGE_FILTER_LIMIT,
      personIds,
    },
    requestPolicy: 'cache-and-network',
    pause: !hasContext || !teamId,
  });
  const choiceBadges = teamId
    ? (teamData?.relevant_badge ?? [])
    : (organizationData?.relevant_badge ?? []);
  const rows = choiceBadges.map(toOrganizationMemberBadgeChoiceRow);
  const visibleRows = filterBySearchText({
    getText: (row) => row.title,
    items: rows,
    searchText: searchTerm,
  });
  const fetching = teamId ? fetchingTeamChoices : fetchingOrganizationChoices;

  const onPressBadge = (row: OrganizationMemberBadgeChoiceRow) => {
    setMemberAssignmentBadgeId(row.id);

    push({
      pathname: '/member-action/assign-badge/details',
    });
  };

  if (!hasContext) {
    return (
      <>
        <Stack.Screen options={{ title: 'Assign Badge' }} />
        <Placeholder.Container>
          <Placeholder.Icon name="people-outline" />
          <Placeholder.Text>Select members to assign a badge.</Placeholder.Text>
        </Placeholder.Container>
      </>
    );
  }

  return (
    <>
      <Stack.Screen options={{ title: 'Choose Badge' }} />
      <FadeInContent fetching={fetching}>
        <Box direction="vertical" flex style={styles.container}>
          <ScrollView
            contentContainerStyle={styles.content}
            contentInsetAdjustmentBehavior="automatic"
            keyboardShouldPersistTaps="always"
          >
            <Box direction="vertical" space="sm" px="md" py="sm">
              <Text variant="typographySecondary" numberOfLines={1}>
                Assigning to {selectedLabel}
              </Text>
              <SearchBar
                value={searchTerm}
                onChangeText={setSearchTerm}
                onPressCancel={() => {
                  setSearchTerm('');
                }}
                placeholder="Search Badges..."
              />
            </Box>
            {visibleRows.length ? (
              visibleRows.map((row) => (
                <PressableRow
                  key={row.id}
                  rounded
                  spaced
                  testID={`${testIDPrefix}BadgeChoice-${row.id}`}
                  onPress={() => {
                    onPressBadge(row);
                  }}
                >
                  <Box direction="horizontal" space="md">
                    <BadgeMask size="sm" color={row.color ?? undefined}>
                      <Avatar
                        ring={false}
                        size="sm"
                        color={row.color ?? ''}
                        name={row.title}
                      />
                    </BadgeMask>
                    <BadgeAssignmentChoiceRow row={row} />
                  </Box>

                  <Icon
                    name="chevron-forward"
                    size="sm"
                    variant="typographySecondary"
                  />
                </PressableRow>
              ))
            ) : (
              <Placeholder.Container>
                <Box style={styles.placeholderIcon}>
                  <Placeholder.Icon name="grid-outline" />
                </Box>
                <Placeholder.Text>
                  {rows.length ? 'No Matching Badges' : 'No Badges Available'}
                </Placeholder.Text>
              </Placeholder.Container>
            )}
          </ScrollView>
        </Box>
      </FadeInContent>
    </>
  );
}

function BadgeAssignmentChoiceRow({
  row,
}: {
  row: OrganizationMemberBadgeChoiceRow;
}) {
  const assignmentLabel =
    row.assignmentCount === 1
      ? '1 assignment'
      : `${row.assignmentCount} assignments`;

  return (
    <Box direction="vertical" flexShrink={1} space="sm">
      <Text bold numberOfLines={1}>
        {row.title}
      </Text>
      <Text numberOfLines={1} size="xs" variant="typographySecondary">
        {assignmentLabel}
      </Text>
    </Box>
  );
}

const styles = StyleSheet.create((theme) => ({
  container: {
    backgroundColor: theme.colors.surface,
  },
  content: {
    flexGrow: 1,
    paddingHorizontal: theme.space('lg'),
    paddingBottom: theme.space('lg'),
  },
  placeholderIcon: {
    transform: [{ rotate: '45deg' }],
  },
}));
